/**
 * userSignatureServiceImpl — saves a base64 signature to S3 and
 * persists the filename to the BP's Cognito profile.
 *
 * Ports the legacy `handleSignatureSubmit` logic from
 * `AssessmentSignature.js` verbatim into the hexagon. Everything
 * happens in two backend calls — Amplify Storage and Amplify Auth —
 * so we keep this service tiny and centralise it for testability.
 */
import type { IUserSignatureProvider } from "../../adapters/driven/providers/userSignatureProvider";

interface IAuthProviderForSignature {
  updateUserAttributes: (
    attributes: Record<string, string>
  ) => Promise<unknown>;
}

export interface IUserSignatureServiceImpl {
  saveUserSignature: ISaveUserSignatureService;
}

/**
 * Convert a data URL or raw base64 string to a PNG Blob. We support
 * both because:
 *   - `ESignSignaturePad.toDataURL()` returns
 *     `data:image/png;base64,...`
 *   - Some legacy code paths persist just the base64 payload
 */
const dataUrlToPngBlob = (input: string): Blob => {
  const base64 = input.includes(",") ? input.split(",")[1] : input;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: "image/png" });
};

/**
 * Build the legacy filename pattern `{userId}-{timestamp}.png`. The
 * timestamp is the millisecond epoch so consecutive saves never
 * collide. Kept identical to the legacy portal so files written from
 * either app land in the same location.
 */
const buildSignatureFileName = (userId: string): string =>
  `${userId}-${Date.now()}.png`;

export const UserSignatureServiceImpl = ({
  userSignatureProvider,
  authProvider,
}: {
  userSignatureProvider: IUserSignatureProvider;
  authProvider: IAuthProviderForSignature;
}): IUserSignatureServiceImpl => ({
  saveUserSignature: async ({ userId, signatureData }) => {
    if (!userId) {
      return {
        success: false,
        error: { message: "Missing userId for signature save" },
      };
    }
    if (!signatureData) {
      return {
        success: false,
        error: { message: "Missing signature data" },
      };
    }
    try {
      const fileName = buildSignatureFileName(userId);
      const png = dataUrlToPngBlob(signatureData);
      await userSignatureProvider.uploadSignature({ fileName, png });
      // Persist the filename to Cognito so the legacy portal and any
      // future modal open picks up the same signature without a re-
      // draw. Failure here would orphan the S3 object but is rare;
      // we still surface it as a soft error so the UI can retry.
      await authProvider.updateUserAttributes({
        "custom:signFileName": fileName,
      });
      return { success: true, data: { fileName } };
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to save user signature";
      return { success: false, error: { message } };
    }
  },
});
